import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FeatherModule } from 'angular-feather';
import { finalize } from 'rxjs';
import { DeletePreviewModalComponent } from '../dialog/delete-preview-modal/delete-preview-modal.component';
import { EditProductModalComponent, EditProductModalResult } from '../dialog/edit-product-modal/edit-product-modal.component';
import { ImagePreviewModalComponent } from '../dialog/image-preview-modal/image-preview-modal.component';
import { AdminImage, AdminImagesService, DeletePreviewResponse } from '../services/api-services/admin-images.service';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

@Component({
  selector: 'app-admin-view-stats',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatTableModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatTooltipModule,
    FeatherModule
  ],
  templateUrl: './admin-view-stats.component.html',
  styleUrl: './admin-view-stats.component.scss'
})
export class AdminViewStatsComponent implements OnInit {
  @ViewChild('tableWrapper') private tableWrapperRef?: ElementRef<HTMLDivElement>;
  filtersForm: FormGroup;
  images: AdminImage[] = [];
  totalImages = 0;
  isLoading = false;
  isInitialLoad = true;
  errorMessage: string | null = null;
  pageSize = PAGE_SIZE_OPTIONS[1];
  pageIndex = 0;
  readonly displayedColumns: (keyof AdminImage | 'actions' | 'delete' | 'edit')[] = [
    'image_name',
    'brand',
    'item',
    'material',
    'color',
    'product_age',
    'product_condition',
    'price',
    'edit',
    'actions',
    'delete'
  ];
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  private rowDownloads = new Set<number>();
  private rowDeletes = new Set<number>();
  private rowPreviews = new Set<number>();
  private rowDeletePreviews = new Set<number>();
  private lastTouchY = 0;
  private lastTouchX = 0;
  private isHandlingVerticalTouch = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly adminImagesService: AdminImagesService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar
  ) {
    this.filtersForm = this.fb.group({
      brand: [''],
      item: [''],
      limit: [this.pageSize]
    });
  }

  ngOnInit(): void {
    console.log('Current theme classes:', document.body.className);
    this.loadImages();
  }

  applyFilters(): void {
    this.pageIndex = 0;
    this.pageSize = this.sanitizeLimit(this.filtersForm.value.limit);
    this.loadImages();
  }

  resetFilters(): void {
    this.filtersForm.reset({
      brand: '',
      item: '',
      limit: PAGE_SIZE_OPTIONS[1]
    });
    this.pageIndex = 0;
    this.pageSize = PAGE_SIZE_OPTIONS[1];
    this.loadImages();
  }

  handlePageEvent(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    const sanitizedPageSize = this.sanitizeLimit(event.pageSize);
    this.pageSize = sanitizedPageSize;
    this.filtersForm.patchValue({ limit: sanitizedPageSize }, { emitEvent: false });
    this.loadImages();
  }

  onTableWheel(event: WheelEvent): void {
    if (!this.tableWrapperRef?.nativeElement) {
      return;
    }

    if (!this.tableWrapperRef.nativeElement.contains(event.target as Node)) {
      return;
    }

    const isHorizontalScroll =
      Math.abs(event.deltaX) > Math.abs(event.deltaY) || (event.shiftKey && event.deltaX !== 0);
    if (isHorizontalScroll) {
      return; // allow horizontal scrolling to proceed normally
    }

    if (event.deltaY === 0) {
      return;
    }

    event.preventDefault();
    this.scrollWindowBy(event.deltaY);
  }

  onTableTouchStart(event: TouchEvent): void {
    this.lastTouchY = event.touches[0]?.clientY ?? 0;
    this.lastTouchX = event.touches[0]?.clientX ?? 0;
    this.isHandlingVerticalTouch = false;
  }

  onTableTouchMove(event: TouchEvent): void {
    if (!this.tableWrapperRef?.nativeElement) {
      return;
    }

    const touch = event.touches[0];
    if (!touch) {
      return;
    }

    const currentY = touch.clientY;
    const currentX = touch.clientX;
    const deltaY = this.lastTouchY - currentY;
    const deltaX = this.lastTouchX - currentX;

    if (!this.isHandlingVerticalTouch) {
      const isHorizontalGesture = Math.abs(deltaX) > Math.abs(deltaY);
      if (isHorizontalGesture) {
        this.lastTouchY = currentY;
        this.lastTouchX = currentX;
        return; // let native horizontal scrolling happen
      }
      this.isHandlingVerticalTouch = true;
    }

    event.preventDefault();
    if (deltaY !== 0) {
      this.scrollWindowBy(deltaY);
    }
    this.lastTouchY = currentY;
    this.lastTouchX = currentX;
  }

  private scrollWindowBy(deltaY: number): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.scrollBy({ top: deltaY, left: 0, behavior: 'auto' });
  }

  isDownloading(imageId: number): boolean {
    return this.rowDownloads.has(imageId);
  }

  isDeleting(imageId: number): boolean {
    return this.rowDeletes.has(imageId);
  }

  isPreviewing(imageId: number): boolean {
    return this.rowPreviews.has(imageId);
  }

  isLoadingDeletePreview(imageId: number): boolean {
    return this.rowDeletePreviews.has(imageId);
  }

  downloadImage(image: AdminImage): void {
    if (!image?.id || this.isDownloading(image.id)) {
      return;
    }

    this.rowDownloads.add(image.id);
    this.adminImagesService
      .getImageDownloadUrl(image.id)
      .pipe(finalize(() => this.rowDownloads.delete(image.id)))
      .subscribe({
        next: response => {
          window.open(response.download_url, '_blank', 'noopener');
        },
        error: err => {
          this.handleError('Failed to fetch download link.', err);
        }
      });
  }

  openDeleteDialog(image: AdminImage): void {
    if (!image?.id || this.isDeleting(image.id) || this.isLoadingDeletePreview(image.id)) {
      return;
    }

    this.rowDeletePreviews.add(image.id);
    this.adminImagesService
      .getDeletePreview(image.id)
      .pipe(finalize(() => this.rowDeletePreviews.delete(image.id)))
      .subscribe({
        next: (preview: DeletePreviewResponse) => {
          const dialogRef = this.dialog.open(DeletePreviewModalComponent, {
            width: '580px',
            maxWidth: '95vw',
            data: preview
          });
          dialogRef.afterClosed().subscribe((confirmed: boolean) => {
            if (confirmed) {
              this.deleteImage(image);
            }
          });
        },
        error: err => {
          this.notifyActionError('Failed to load delete preview.', err);
        }
      });
  }

  private loadImages(): void {
    const formValues = this.filtersForm.value;
    const limit = this.sanitizeLimit(formValues.limit);
    const offset = this.pageIndex * limit;

    this.isLoading = true;
    this.errorMessage = null;

    this.adminImagesService
      .getImages({
        limit,
        offset,
        brand: formValues.brand?.trim() || undefined,
        item: formValues.item?.trim() || undefined
      })
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.isInitialLoad = false;
        })
      )
      .subscribe({
        next: response => {
          this.images = response.images;
          this.totalImages = response.total;
          this.pageSize = response.limit;
          const derivedPageIndex =
            response.limit ? Math.floor(response.offset / response.limit) : 0;
          this.pageIndex = Number.isFinite(derivedPageIndex) ? derivedPageIndex : 0;
          if (this.filtersForm.value.limit !== response.limit) {
            this.filtersForm.patchValue({ limit: response.limit }, { emitEvent: false });
          }
        },
        error: err => {
          this.images = [];
          this.totalImages = 0;
          this.handleError('Unable to load images.', err);
        }
      });
  }

  private sanitizeLimit(rawLimit: unknown): number {
    const numericLimit = Number(rawLimit) || PAGE_SIZE_OPTIONS[1];
    return Math.max(1, Math.min(1000, numericLimit));
  }

  private deleteImage(image: AdminImage): void {
    if (!image?.id || this.isDeleting(image.id)) {
      return;
    }

    this.rowDeletes.add(image.id);
    this.adminImagesService
      .deleteImage(image.id)
      .pipe(finalize(() => this.rowDeletes.delete(image.id)))
      .subscribe({
        next: () => {
          this.snackBar.open('Image deleted.', 'Dismiss', {
            duration: 4000,
            horizontalPosition: 'end',
            verticalPosition: 'top'
          });
          this.loadImages();
        },
        error: err => {
          this.notifyActionError('Failed to delete image.', err);
        }
      });
  }

  private handleError(message: string, error: unknown): void {
    this.errorMessage = message;
    this.snackBar.open(message, 'Dismiss', {
      duration: 4000,
      horizontalPosition: 'end',
      verticalPosition: 'top'
    });
    console.error(message, error);
  }

  private notifyActionError(message: string, error: unknown): void {
    this.snackBar.open(message, 'Dismiss', {
      duration: 4000,
      horizontalPosition: 'end',
      verticalPosition: 'top'
    });
    console.error(message, error);
  }

  previewImage(image: AdminImage): void {
    if (!image?.id || this.isPreviewing(image.id)) {
      return;
    }

    this.rowPreviews.add(image.id);
    this.adminImagesService
      .getImageDownloadUrl(image.id)
      .pipe(finalize(() => this.rowPreviews.delete(image.id)))
      .subscribe({
        next: response => {
          const name = image.image_name?.split('/')?.pop() ?? image.image_name ?? 'Image';
          this.dialog.open(ImagePreviewModalComponent, {
            width: '860px',
            maxWidth: '95vw',
            data: { url: response.download_url, name }
          });
        },
        error: err => {
          this.notifyActionError('Failed to fetch image preview.', err);
        }
      });
  }

  openEditDialog(image: AdminImage): void {
    if (!image?.id) return;

    const dialogRef = this.dialog.open(EditProductModalComponent, {
      width: '680px',
      maxWidth: '95vw',
      data: { product: image, title: 'Edit Product' }
    });

    dialogRef.afterClosed().subscribe((result: EditProductModalResult | null) => {
      if (!result) return;
      this.adminImagesService.editImage(image.id, result)
        .subscribe({
          next: response => {
            const idx = this.images.findIndex(i => i.id === image.id);
            if (idx !== -1) {
              this.images[idx] = response.product;
              this.images = [...this.images];
            }
            this.snackBar.open('Product updated.', 'Dismiss', {
              duration: 3000, horizontalPosition: 'end', verticalPosition: 'top'
            });
          },
          error: err => this.notifyActionError('Failed to update product.', err)
        });
    });
  }
}
